import React, { useEffect, useState } from 'react';
import moment from 'moment';
import capitalize from 'capitalize';
import _ from 'lodash';
import { InputAdornment } from '@material-ui/core';
import {
    ArrayInput,
    Create,
    FormDataConsumer,
    SelectInput,
    TabbedForm,
    FormTab,
    TextInput,
    ReferenceInput,
    DateInput,
    BooleanInput,
    AutocompleteInput,
    SelectArrayInput,
    SimpleFormIterator,
    RadioButtonGroupInput,
    NumberInput,
    useDataProvider,
    regex,
} from 'react-admin';
import {
    PaymentMethod,
    StorageType,
    OrderType,
    CountryCode,
    CurrencyByCountryId,
} from '../constants';
import MoneyInput from '../components/MoneyInput';
import BillingCard from '../orders/components/BillingCard';
import ItemsInput from './components/ItemsInput';
import BoxitPhoneInput from '../components/BoxitPhoneInput';
import WhenFieldChanges from '../components/WhenFieldChanges';
import { AppState, CreateProps, Order, ValidationErrors } from '../types';
import PlaceAutoComplete from '../components/PlaceAutoComplete';
import { Source } from '../constants';
import { connect } from 'react-redux';
import useLocalStorage from '../hooks/useLocalStorage';
import { useGetOne } from 'react-admin';
import { useSelector } from 'react-redux';
import useIsAdmin from '../hooks/useIsAdmin';
import PartnerInput from '../components/PartnerInput';
import CountryInput from '../components/CountryInput';
import CityInput from '../components/CityInput';
import CurrencyInput from '../components/CurrencyInput';
import { useForm, useFormState } from 'react-final-form';

const optionText = user => `${user.name} (${user.id})`;

// Define name validation to prevent special characters
const nameValidation = regex(
    /^[\u0600-\u06FFa-zA-Z0-9\s]*$/,
    "Name cannot contain special characters like @ or '"
);

const hasBoxitBoxes = (formData: any): boolean => {
    return (
        formData.items &&
        formData.items.some(
            i =>
                i &&
                i.storagetypeId === StorageType.BB &&
                Number(i.quantity) > 0
        )
    );
};

const PartnerPrefill = () => {
    const form = useForm();
    const { values } = useFormState();
    const dataProvider = useDataProvider();
    const isAdmin = useIsAdmin();

    useEffect(() => {
        if (!values.partnerId) return;

        const isNewCustomer = !values.existingCustomer;
        const isBoxitPartner = values.partnerId === 1;

        // Exception: Boxit (id=1) on create/new-customer - do NOT force country from partner
        if (isNewCustomer && isBoxitPartner) return;

        // Exception: Boxit (id=1) with existing customer - prefill from customer data
        if (!isNewCustomer && isBoxitPartner && values.userId) {
            dataProvider
                .getOne('users', { id: values.userId })
                .then(({ data: userData }) => {
                    const customerCountryId = userData?.countryId;

                    if (customerCountryId) {
                        form.change('countryId', customerCountryId);

                        // Get currency from customer's country
                        if (userData?.currency) {
                            form.change('currency', userData.currency);
                        } else if (CurrencyByCountryId[customerCountryId]) {
                            form.change(
                                'currency',
                                CurrencyByCountryId[customerCountryId]
                            );
                        } else {
                            dataProvider
                                .getOne('country', { id: customerCountryId })
                                .then(({ data: country }) => {
                                    if (country?.currency) {
                                        form.change(
                                            'currency',
                                            country.currency
                                        );
                                    }
                                })
                                .catch(err =>
                                    console.error(
                                        'Error fetching country:',
                                        err
                                    )
                                );
                        }

                        // Get country code from customer's country
                        if (CountryCode[customerCountryId]) {
                            form.change(
                                'countryCode',
                                CountryCode[customerCountryId]
                            );
                        }
                    }
                })
                .catch(error => {
                    console.error('Error fetching user details:', error);
                });
            return;
        }

        // Default behavior: prefill from partner data
        dataProvider
            .getOne('partners', { id: values.partnerId })
            .then(({ data }) => {
                const partnerCountryId = data?.countryId || data?.country?.id;

                if (partnerCountryId) {
                    form.change('countryId', partnerCountryId);
                }

                if (data?.currency || data?.country?.currency) {
                    form.change(
                        'currency',
                        data.currency || data.country?.currency
                    );
                } else if (partnerCountryId) {
                    dataProvider
                        .getOne('country', { id: partnerCountryId })
                        .then(({ data: country }) => {
                            if (country?.currency) {
                                form.change('currency', country.currency);
                            }
                        })
                        .catch(err =>
                            console.error('Error fetching country:', err)
                        );
                }

                if (data?.countryCode || data?.country?.code) {
                    form.change(
                        'countryCode',
                        data.countryCode || data.country?.code
                    );
                } else if (partnerCountryId && CountryCode[partnerCountryId]) {
                    form.change('countryCode', CountryCode[partnerCountryId]);
                }
            })
            .catch(error => {
                console.error('Error fetching partner details:', error);
            });
    }, [values.partnerId, values.userId, values.existingCustomer]);

    return null;
};

// Exception: if Partner is Boxit (id=1) AND this is New Customer, allow editing country,
// and derive currency + countryCode from the chosen country (locked fields).
const BoxitCountryDerivedPrefill = () => {
    const form = useForm();
    const { values } = useFormState();
    const dataProvider = useDataProvider();
    const isAdmin = useIsAdmin();

    useEffect(() => {
        const isNewCustomer = !values.existingCustomer;
        const isBoxitPartner = values.partnerId === 1;

        if (!isNewCustomer || !isBoxitPartner || !values.countryId) return;

        dataProvider
            .getOne('country', { id: values.countryId })
            .then(({ data: country }) => {
                if (country?.currency) {
                    form.change('currency', country.currency);
                }

                if (country?.code) {
                    form.change('countryCode', country.code);
                } else if (CountryCode[values.countryId]) {
                    form.change('countryCode', CountryCode[values.countryId]);
                }
            })
            .catch(err => console.error('Error fetching country:', err));
    }, [values.partnerId, values.countryId, values.existingCustomer]);

    return null;
};

const ExistingCustomerPartnerIdPrefill = () => {
    const form = useForm();
    const { values } = useFormState();
    const dataProvider = useDataProvider();
    const isAdmin = useIsAdmin();

    useEffect(() => {
        if (!values.existingCustomer || !values.userId) {
            return;
        }

        dataProvider
            .getOne('users', { id: values.userId })
            .then(({ data }) => {
                if (data?.partnerId) {
                    form.change('partnerId', data.partnerId);
                }
            })
            .catch(error => {
                console.error('Error fetching user details:', error);
            });
    }, [values.existingCustomer, values.userId]);

    return null;
};

const validate = values => {
    const errors: ValidationErrors = {};

    if (!values.cityId) {
        errors.cityId = ['City is required'];
    }

    if (values.months > 36) {
        errors.months = ['Must be less than or equal to 36'];
    }

    if (values.existingCustomer && !values.userId) {
        errors.userId = ['User is required'];
    }

    if (values.partnerId === 1 && !values.channel) {
        errors.channel = ['Channel is required'];
    }

    if (!values.existingCustomer && !values.partnerId) {
        errors.partnerId = ['Partner is required'];
    }

    if (!values.source && !values.existingCustomer) {
        errors.source = ['errors.required'];
    }

    return errors;
};

const transform = data => {
    console.log('data', data);
    data.partnerId = data.partnerId || data._partnerId || 1;
    const countryId = localStorage.getItem('countryId');
    return {
        ...data,
        warehouseType:
            countryId && countryId === '2' && !data.warehouseType
                ? ['ac']
                : data.warehouseType,
        source: data.source || 'existingCustomer',
    };
};

// const transform = data => {
//     console.log("data",data)
//     console.log("selectedValue",selectedValue)
//     const countryId = localStorage.getItem('countryId');
//     return {
//         ...data,
//         warehouseType:
//             countryId && countryId === '2' && !data.warehouseType
//                 ? ['ac']
//                 : data.warehouseType,
//         source: selectedValue || data.source,
//     };
// };
const OrderCreate: React.FC<CreateProps<Order>> = ({
    permissions = [],
    ...props
}) => {
    const isAdmin = useIsAdmin(permissions);
    const [currentUserId] = useLocalStorage('userId', null);
    const { data: currentUser } = useGetOne('users', currentUserId);
    const dataProvider = useDataProvider();
    const [selectedValue, setSelectedValue] = React.useState('');
    const [selectedUser, setSelectedUser] = useState<any>(null);
    // Store the partner ID from the selected user
    const [userPartnerId, setUserPartnerId] = useState<number | null>(null);

    const handleChange = event => {
        setSelectedValue(event.target.value);
    };

    const selectedCustomer = useSelector((state: any) => {
        const customerId =
            state.admin?.resources?.orders?.list?.params?.filter?.userId;
        const customerDto = state.admin?.resources?.users.data?.[customerId];
        return customerDto;
    });

    const mostRecentAddress = useSelector((state: any) => {
        const addresses =
            state.admin?.references?.possibleValues?.['addresses@addressId']
                ?.map(
                    addressId =>
                        state.admin?.resources?.addresses?.data[addressId]
                )
                .sort(
                    (a, b) =>
                        new Date(b.createdAt).getTime() -
                        new Date(a.createdAt).getTime()
                ) || [];
        return addresses[0] || null;
    });
    const handleChangevalue = value => {
        if (value) {
            // Fetch user details using dataProvider
            dataProvider
                .getOne('users', { id: value })
                .then(({ data: userData }) => {
                    // Update the source based on user status
                    const sourceValue = userData.existingCustomer
                        ? 'existingCustomer'
                        : userData.exCustomer
                        ? 'exCustomer'
                        : '';
                    setSelectedValue(sourceValue);

                    // Store the user data to access partnerId
                    setSelectedUser(userData);

                    // Store the user's partnerId separately
                    if (userData.partnerId) {
                        console.log(
                            'Setting user partnerId to:',
                            userData.partnerId
                        );
                        setUserPartnerId(userData.partnerId);
                    }
                })
                .catch(error => {
                    console.error('Error fetching user details:', error);
                });
        } else {
            // Clear selected user when no value
            setSelectedUser(null);
            setUserPartnerId(null);
        }
    };
    useEffect(() => {
        if (selectedCustomer?.id) {
            handleChangevalue(selectedCustomer.id);
        }
    }, [selectedCustomer]);

    return (
        <Create
            successMessage="resources.orders.notification.create_success"
            {...props}
            transform={transform}
        >
            <TabbedForm
                variant="outlined"
                validate={validate}
                redirect="show"
                {...props}
            >
                <FormTab label="Storage">
                    <TextInput
                        disabled
                        {...props}
                        source="status"
                        defaultValue="confirmed"
                        style={{ display: 'none' }}
                    />
                    <DateInput
                        label="Confirmed At"
                        source="confirmedAt"
                        defaultValue={new Date()}
                        style={{ display: 'none' }}
                    />
                    <BooleanInput
                        style={{ display: 'none' }}
                        source="skipSignupMail"
                        defaultValue={true}
                    />
                    <BooleanInput
                        style={{ display: 'none' }}
                        source="skipOrderMail"
                        defaultValue={true}
                    />

                    <SelectInput
                        source="type"
                        label="Order Type"
                        defaultValue={OrderType.storage}
                        choices={Object.keys(OrderType).map(i => ({
                            id: i,
                            name: capitalize(i.replace('_', ' ')),
                        }))}
                    />

                    {/* Partner + Country/Currency/Code prefill & lock */}
                    {(permissions.indexOf('staff') !== -1 || isAdmin) && (
                        <FormDataConsumer>
                            {({ formData }) => {
                                const lockDerived = !!formData.partnerId;
                                const isBoxit = formData.partnerId === 1;
                                const isNewCustomer = !formData.existingCustomer;
                                const lockCountry =
                                    !!formData.partnerId &&
                                    (!isNewCustomer || !isBoxit);
                                const disableCountry = !isAdmin && lockCountry;
                                const disableCurrency = !isAdmin && lockDerived;
                                const disableCountryCode =
                                    !isAdmin && lockDerived;
                                const disablePartner =
                                    !isAdmin && !!formData.existingCustomer;

                                return (
                                    <>
                                        <ExistingCustomerPartnerIdPrefill />
                                        <PartnerPrefill />
                                        <BoxitCountryDerivedPrefill />

                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '0.75rem',
                                                width: '256px',
                                            }}
                                        >
                                            <ReferenceInput
                                                label="Partner"
                                                reference="partners"
                                                resource="partners"
                                                source="partnerId"
                                            >
                                                <SelectInput
                                                    optionText="name"
                                                    disabled={disablePartner}
                                                />
                                            </ReferenceInput>

                                            <CountryInput
                                                defaultValue={
                                                    currentUser?.countryId
                                                }
                                                disabled={disableCountry}
                                            />

                                            <CityInput
                                                defaultValue={
                                                    currentUser?.countryId === 2
                                                        ? 6
                                                        : ''
                                                }
                                            />

                                            <CurrencyInput
                                                defaultValue={
                                                    CurrencyByCountryId[
                                                        currentUser?.countryId
                                                    ]
                                                }
                                                disabled={disableCurrency}
                                            />

                                            <TextInput
                                                variant="outlined"
                                                source="countryCode"
                                                label="Country Code"
                                                fullWidth
                                                style={{ width: '256px' }}
                                                disabled={disableCountryCode}
                                            />
                                        </div>
                                    </>
                                );
                            }}
                        </FormDataConsumer>
                    )}

                    <div style={{ marginBottom: '1rem' }}>
                        <TextInput
                            source="months"
                            label="Duration"
                            defaultValue={1}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="start">
                                        Months
                                    </InputAdornment>
                                ),
                            }}
                            fullWidth={true}
                            style={{ width: '256px' }}
                        />
                    </div>

                    <div>
                        <NumberInput
                            source="expectedStorageDuration"
                            label="Expected Storage Duration"
                            defaultValue={1}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="start">
                                        Months
                                    </InputAdornment>
                                ),
                            }}
                            fullWidth={true}
                            style={{ width: '256px' }}
                        />
                    </div>

                    <SelectInput
                        source="paymentMode"
                        label="Payment Mode"
                        choices={Object.keys(PaymentMethod).map(i => ({
                            id: i,
                            name: capitalize(i),
                        }))}
                    />

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            formData.type === OrderType.storage && (
                                <SelectInput
                                    variant="outlined"
                                    label={
                                        currentUser?.countryId === 2
                                            ? 'AC'
                                            : 'Warehouse Type'
                                    }
                                    source="warehouseType"
                                    choices={[
                                        { id: 'ac', name: 'AC' },
                                        { id: 'non-ac', name: 'Non AC' },
                                    ]}
                                    parse={v => v && [v]}
                                    fullWidth={true}
                                    style={{ width: '256px' }}
                                />
                            )
                        }
                    </FormDataConsumer>
                </FormTab>

                <FormTab label="Customer">
                    <BooleanInput
                        source="existingCustomer"
                        label="This is for an existing Customer"
                        defaultValue={!!selectedCustomer?.id || false}
                    />

                    <FormDataConsumer>
                        {({ formData, getSource, scopedFormData, ...rest }) =>
                            !!formData.existingCustomer && (
                                <ReferenceInput
                                    label="User"
                                    source="userId"
                                    reference="users"
                                    resource="users"
                                    defaultValue={selectedCustomer?.id}
                                    onChange={handleChangevalue}
                                >
                                    <AutocompleteInput
                                        shouldRenderSuggestions={val =>
                                            val.trim().length > 1
                                        }
                                        optionText={optionText}
                                    />
                                </ReferenceInput>
                            )
                        }
                    </FormDataConsumer>

                    {_.intersection(permissions, [
                        'admin',
                        'staff',
                        'global-staff',
                    ]).length > 0 && [
                        <SelectArrayInput
                            key="tags"
                            source="tags"
                            choices={[{ id: 'admin', name: 'admin' }]}
                            defaultValue={['admin']}
                            style={{ display: 'none' }}
                        />,
                        <SelectInput
                            key="channel"
                            source="channel"
                            defaultValue={
                                selectedCustomer ? 'Existing Customer' : ''
                            }
                            choices={[
                                { id: 'google', name: 'Google' },
                                { id: 'youtube', name: 'YouTube' },
                                { id: 'instagram', name: 'Instagram' },
                                { id: 'existing', name: 'Existing Customer' },
                                { id: 'friend', name: 'Referred by friend' },
                                { id: 'facebook', name: 'Facebook' },
                                { id: 'tv', name: 'TV' },
                                { id: 'blog', name: 'Blog' },
                                { id: 'event', name: 'Event' },
                                { id: 'flyer', name: 'Flyer' },
                                { id: 'press', name: 'Press / News' },
                                { id: 'signage', name: 'Signage' },
                                { id: 'truck', name: 'Truck' },
                                { id: 'partner', name: 'Partner' },
                                { id: 'other', name: 'Other' },
                            ]}
                        />,
                        <SelectInput
                            key="source"
                            source="source"
                            value={selectedValue}
                            onChange={handleChange}
                            choices={Object.keys(Source).map(c => ({
                                id: c,
                                name: Source[c],
                            }))}
                        />,
                    ]}
                    <TextInput
                        label="Reference"
                        source="ref"
                        // defaultValue={selectedCustomer?.id}
                    />

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            !formData.existingCustomer && (
                                <TextInput
                                    variant="outlined"
                                    source="name"
                                    label="Customer Name"
                                    defaultValue={selectedCustomer?.name}
                                    validate={nameValidation}
                                />
                            )
                        }
                    </FormDataConsumer>
                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            !formData.existingCustomer && (
                                <TextInput
                                    variant="outlined"
                                    source="email"
                                    type="email"
                                    label="Customer Email"
                                    defaultValue={selectedCustomer?.email}
                                />
                            )
                        }
                    </FormDataConsumer>
                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            !formData.existingCustomer && (
                                <BoxitPhoneInput
                                    source="phone"
                                    label="Customer Phone"
                                    defaultValue={selectedCustomer?.phone}
                                    country={formData.countryId}
                                    defaultCountry={currentUser?.countryId}
                                />
                            )
                        }
                    </FormDataConsumer>

                    <TextInput multiline source="notes" />
                </FormTab>

                <FormTab label="Address">
                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            (formData.type === OrderType.moving ||
                                formData.type === OrderType.shipping ||
                                formData.type === OrderType.other_services) && (
                                <h3 className="mb-4 text-lg leading-6 font-medium text-gray-800">
                                    Source Address
                                </h3>
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            !!formData.userId && (
                                <ReferenceInput
                                    label="Address"
                                    source="addressId"
                                    reference="addresses"
                                    resource="addresses"
                                    filter={{ userId: formData.userId }}
                                    defaultValue={mostRecentAddress?.id}
                                >
                                    <SelectInput
                                        variant="outlined"
                                        source="addressId"
                                        optionText={addr =>
                                            `${addr.line1}, ${addr.area}, ${addr.cityId}`
                                        }
                                    />
                                </ReferenceInput>
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            !formData.addressId && (
                                <TextInput
                                    variant="outlined"
                                    source="line1"
                                    label="Address Line"
                                    fullWidth={true}
                                    style={{ width: '256px' }}
                                />
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            !formData.addressId && (
                                <PlaceAutoComplete source="area" />
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            (formData.type === OrderType.moving ||
                                formData.type === OrderType.shipping ||
                                formData.type === OrderType.other_services) && (
                                <h3 className="mb-4 text-lg leading-6 font-medium text-gray-800">
                                    Destination Address
                                </h3>
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            !!formData.userId &&
                            (formData.type === OrderType.moving ||
                                formData.type === OrderType.shipping ||
                                formData.type === OrderType.other_services) && (
                                <ReferenceInput
                                    label="Address"
                                    source="destAddressId"
                                    reference="addresses"
                                    resource="addresses"
                                    filter={{ userId: formData.userId }}
                                >
                                    <SelectInput
                                        variant="outlined"
                                        source="destAddressId"
                                        optionText={addr =>
                                            `${addr.line1}, ${addr.area}, ${addr.cityId}`
                                        }
                                    />
                                </ReferenceInput>
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            !formData.destAddressId &&
                            (formData.type === OrderType.moving ||
                                formData.type === OrderType.shipping ||
                                formData.type === OrderType.other_services) && (
                                <TextInput
                                    variant="outlined"
                                    source="destLine1"
                                    label="Address Line"
                                    fullWidth={true}
                                    style={{ width: '256px' }}
                                />
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            !formData.destAddressId &&
                            (formData.type === OrderType.moving ||
                                formData.type === OrderType.shipping ||
                                formData.type === OrderType.other_services) && (
                                <TextInput
                                    variant="outlined"
                                    source="destArea"
                                    label="Area"
                                    fullWidth={true}
                                    style={{ width: '256px' }}
                                />
                            )
                        }
                    </FormDataConsumer>
                </FormTab>

                <FormTab label="Items">
                    <FormDataConsumer>
                        {formDataProps => <ItemsInput {...formDataProps} />}
                    </FormDataConsumer>
                </FormTab>

                <FormTab label="Services">
                    <ArrayInput source="services" label="">
                        <SimpleFormIterator>
                            <ReferenceInput
                                label="service"
                                source="servicetypeId"
                                reference="service-types"
                                resource="service-types"
                                sort={{ field: 'name', order: 'ASC' }}
                            >
                                <SelectInput source="id" label="Service" />
                            </ReferenceInput>
                            <MoneyInput source="amount" label="charge" />
                        </SimpleFormIterator>
                    </ArrayInput>
                </FormTab>

                <FormTab label="Date">
                    <RadioButtonGroupInput
                        label="Type of Operation"
                        source="pickupRequired"
                        defaultValue={true}
                        row={true}
                        choices={[
                            { id: true, name: 'Pickup' },
                            { id: false, name: 'Drop Off' },
                        ]}
                    />

                    <FormDataConsumer>
                        {({ formData, ...rest }) => (
                            <DateInput
                                variant="outlined"
                                label={
                                    formData.pickupRequired
                                        ? 'Pickup Date'
                                        : 'Drop Date'
                                }
                                source="date"
                                parse={v => v && moment(v).format('YYYY-MM-DD')}
                                style={{
                                    display: 'inline',
                                    float: 'left',
                                }}
                            />
                        )}
                    </FormDataConsumer>

                    <SelectInput
                        variant="outlined"
                        label="Time Slot"
                        source="pickupTimeFrom"
                        choices={[
                            {
                                id: 9,
                                name: '9 am - 1 pm',
                            },
                            {
                                id: 13,
                                name: '1 pm - 6 pm',
                            },
                        ]}
                        translateChoice={false}
                        style={{
                            marginLeft: '20px',
                        }}
                    />

                    <NumberInput
                        style={{ display: 'none' }}
                        source="pickupTimeTo"
                    />

                    <WhenFieldChanges
                        field="pickupTimeFrom"
                        becomes={9}
                        set="pickupTimeTo"
                        to={13}
                    />
                    <WhenFieldChanges
                        field="pickupTimeFrom"
                        becomes={13}
                        set="pickupTimeTo"
                        to={18}
                    />

                    <FormDataConsumer>
                        {({ record, ...rest }) =>
                            hasBoxitBoxes(record) && <hr />
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ record, ...rest }) =>
                            hasBoxitBoxes(record) && (
                                <BooleanInput
                                    source="newboxRequired"
                                    label="Deliver Boxit Boxes Early"
                                    defaultValue={false}
                                />
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ record, ...rest }) =>
                            hasBoxitBoxes(record) &&
                            !!record.newboxRequired && (
                                <DateInput
                                    label="Box Delivery Date"
                                    source="deliveryDate"
                                    parse={v =>
                                        v && moment(v).format('YYYY-MM-DD')
                                    }
                                />
                            )
                        }
                    </FormDataConsumer>
                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            hasBoxitBoxes(formData) &&
                            !!formData.newboxRequired && (
                                <SelectInput
                                    variant="outlined"
                                    label="Time Slot"
                                    source="deliveryTimeFrom"
                                    choices={[
                                        {
                                            id: 9,
                                            name: '9 am - 1 pm',
                                        },
                                        {
                                            id: 13,
                                            name: '1 pm - 6 pm',
                                        },
                                    ]}
                                    translateChoice={false}
                                    style={{
                                        marginLeft: '20px',
                                    }}
                                />
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            hasBoxitBoxes(formData) &&
                            !!formData.newboxRequired && (
                                <NumberInput
                                    style={{ display: 'none' }}
                                    source="deliveryTimeTo"
                                />
                            )
                        }
                    </FormDataConsumer>

                    <WhenFieldChanges
                        field="deliveryTimeFrom"
                        becomes={9}
                        set="deliveryTimeTo"
                        to={13}
                    />
                    <WhenFieldChanges
                        field="deliveryTimeFrom"
                        becomes={13}
                        set="deliveryTimeTo"
                        to={18}
                    />
                </FormTab>

                <FormTab label="Checklist">
                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            formData.pickupRequired && (
                                <SelectInput
                                    source="checklist.buildingType"
                                    label="Type of Building"
                                    style={{ width: 300 }}
                                    choices={[
                                        { id: 'Flat', name: 'Flat' },
                                        { id: 'Villa', name: 'Villa' },
                                        { id: 'Hotel', name: 'Hotel' },
                                        { id: 'Office', name: 'Office' },
                                        { id: 'Other', name: 'Other' },
                                    ]}
                                />
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            formData.pickupRequired && (
                                <RadioButtonGroupInput
                                    label="Is entry permit required"
                                    source="checklist.permitRequired"
                                    row={true}
                                    choices={[
                                        { id: true, name: 'Yes' },
                                        { id: false, name: 'No' },
                                    ]}
                                />
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            formData.pickupRequired && (
                                <RadioButtonGroupInput
                                    label="Is moving insurance required"
                                    source="checklist.movingInsuranceRequired"
                                    row={true}
                                    choices={[
                                        { id: true, name: 'Yes' },
                                        { id: false, name: 'No' },
                                    ]}
                                />
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            formData.pickupRequired && (
                                <RadioButtonGroupInput
                                    label="Is parking slot available"
                                    source="checklist.parkingSlotAvailable"
                                    row={true}
                                    choices={[
                                        { id: true, name: 'Yes' },
                                        { id: false, name: 'No' },
                                    ]}
                                />
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            formData.pickupRequired &&
                            !!formData.checklist &&
                            !!formData.checklist.parkingSlotAvailable && (
                                <RadioButtonGroupInput
                                    label="Is parking slot inside or outside the building"
                                    source="checklist.isSlotInsideOrOutside"
                                    row={true}
                                    fullWidth={true}
                                    choices={[
                                        {
                                            id: 'Inside',
                                            name: 'Inside',
                                        },
                                        {
                                            id: 'Outside',
                                            name: 'Outside',
                                        },
                                    ]}
                                />
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            formData.pickupRequired &&
                            !!formData.checklist &&
                            formData.checklist.parkingSlotAvailable && (
                                <SelectInput
                                    source="checklist.maximumAllowedLoadingTime"
                                    label="Maximum allowed Loading time"
                                    style={{ width: 300 }}
                                    choices={[
                                        {
                                            id: 'Unlimitted',
                                            name: 'Unlimitted',
                                        },
                                        { id: '1 Hr', name: '1 Hr' },
                                        { id: '2 Hr', name: '2 Hr' },
                                        { id: '3 Hr', name: '3 Hr' },
                                        { id: '4 Hr', name: '4 Hr' },
                                        { id: '5 Hr', name: '5 Hr' },
                                        { id: '6 Hr', name: '6 Hr' },
                                        { id: '7 Hr', name: '7 Hr' },
                                        { id: '8 Hr', name: '8 Hr' },
                                    ]}
                                />
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            formData.pickupRequired && (
                                <TextInput
                                    source="checklist.buildingFloor"
                                    label="Building Floor"
                                    style={{ width: 300 }}
                                />
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            formData.pickupRequired && (
                                <RadioButtonGroupInput
                                    label="Is lift available"
                                    source="checklist.liftAvailable"
                                    row={true}
                                    choices={[
                                        { id: true, name: 'Yes' },
                                        { id: false, name: 'No' },
                                    ]}
                                />
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            formData.pickupRequired &&
                            !!formData.checklist &&
                            !!formData.checklist.liftAvailable && (
                                <SelectInput
                                    source="checklist.maximumAllowedLiftTime"
                                    label="Maximum allowed time for Lift"
                                    style={{ width: 300 }}
                                    choices={[
                                        {
                                            id: 'Unlimitted',
                                            name: 'Unlimitted',
                                        },
                                        { id: '1 Hr', name: '1 Hr' },
                                        { id: '2 Hr', name: '2 Hr' },
                                        { id: '3 Hr', name: '3 Hr' },
                                        { id: '4 Hr', name: '4 Hr' },
                                        { id: '5 Hr', name: '5 Hr' },
                                        { id: '6 Hr', name: '6 Hr' },
                                        { id: '7 Hr', name: '7 Hr' },
                                        { id: '8 Hr', name: '8 Hr' },
                                    ]}
                                />
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            formData.pickupRequired &&
                            !!formData.checklist &&
                            !!formData.checklist.liftAvailable && (
                                <RadioButtonGroupInput
                                    label="Are there items that would not fit inside the lift"
                                    source="checklist.anyItemsNotFitinsideLift"
                                    fullWidth={true}
                                    row={true}
                                    choices={[
                                        { id: true, name: 'Yes' },
                                        { id: false, name: 'No' },
                                    ]}
                                />
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            formData.pickupRequired &&
                            !!formData.checklist &&
                            !!formData.checklist.anyItemsNotFitinsideLift && (
                                <TextInput
                                    multiline
                                    style={{ width: 300 }}
                                    label="If yes details"
                                    source="checklist.liftNotFitDetails"
                                />
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            formData.pickupRequired && (
                                <RadioButtonGroupInput
                                    label="If customer wants to authorize someone"
                                    source="checklist.authorizeOtherPerson"
                                    fullWidth={true}
                                    row={true}
                                    choices={[
                                        { id: true, name: 'Yes' },
                                        { id: false, name: 'No' },
                                    ]}
                                />
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            formData.pickupRequired &&
                            !!formData.checklist &&
                            !!formData.checklist.authorizeOtherPerson && (
                                <TextInput
                                    label="Authorized Person Name"
                                    source="checklist.authorizedName"
                                    style={{ width: 300 }}
                                />
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            formData.pickupRequired &&
                            !!formData.checklist &&
                            !!formData.checklist.authorizeOtherPerson && (
                                <BoxitPhoneInput
                                    source="checklist.authorizedPhone"
                                    label="Authorized Person Phone"
                                    style={{ width: 300 }}
                                    country={formData.countryId}
                                    defaultCountry={currentUser?.countryId}
                                />
                            )
                        }
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData, ...rest }) =>
                            formData.pickupRequired &&
                            !!formData.checklist &&
                            !!formData.checklist.authorizeOtherPerson && (
                                <TextInput
                                    label="Authorized Person ID Number"
                                    source="checklist.authorizedID"
                                    style={{ width: 300 }}
                                />
                            )
                        }
                    </FormDataConsumer>
                </FormTab>

                <FormTab label="Billing">
                    <FormDataConsumer>
                        {({ formData, ...rest }) => (
                            <BillingCard record={formData} />
                        )}
                    </FormDataConsumer>
                </FormTab>
            </TabbedForm>
        </Create>
    );
};

const mapStateToProps = (state: AppState, props) => {
    // const invoiceId = props.data && props.data.invoiceId;
    // console.log(state);
    return state;
};
const connector = connect(mapStateToProps);

// export default OrderCreate;

export default connector(OrderCreate);
